import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { jiraApi } from '@/api/jira'
import { useCurrentTenant } from '@/store/auth'

export function useJiraSummary() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-summary', tenantId],
    queryFn: () => jiraApi.getSummary(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

export function useJiraIssues(params?: Parameters<typeof jiraApi.getIssues>[1]) {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-issues', tenantId, params],
    queryFn: () => jiraApi.getIssues(tenantId!, params),
    enabled: !!tenantId,
    staleTime: 30_000,
  })
}

export function useJiraConfig() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-config', tenantId],
    queryFn: () => jiraApi.getConfig(tenantId!),
    enabled: !!tenantId,
    retry: false,
  })
}

export function useJiraSyncStatus() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-sync-status', tenantId],
    queryFn: () => jiraApi.getSyncStatus(tenantId!),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export function useJiraStatusMappings() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-status-mappings', tenantId],
    queryFn: () => jiraApi.getStatusMappings(tenantId!),
    enabled: !!tenantId,
  })
}

export function useJiraUserMappings() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-user-mappings', tenantId],
    queryFn: () => jiraApi.getUserMappings(tenantId!),
    enabled: !!tenantId,
  })
}

export function useJiraIssueDetail(jiraKey: string | null | undefined) {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-issue-detail', tenantId, jiraKey],
    queryFn: () => jiraApi.getIssueDetail(tenantId!, jiraKey!),
    enabled: !!tenantId && !!jiraKey,
  })
}

export function useJiraIssueComments(jiraKey: string | null | undefined) {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-issue-comments', tenantId, jiraKey],
    queryFn: () => jiraApi.getIssueComments(tenantId!, jiraKey!),
    enabled: !!tenantId && !!jiraKey,
    staleTime: 60_000,
  })
}

export function useJiraConflicts() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-conflicts', tenantId],
    queryFn: () => jiraApi.getConflicts(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  })
}

export function useJiraWebSocket() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const queryClient = useQueryClient()
  const [isLive, setIsLive] = useState(false)

  useEffect(() => {
    if (!tenantId) return

    const token = localStorage.getItem('session_token') || ''
    if (!token) return

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/api/v1/tenants/${tenantId}/jira/ws?token=${token}`

    let ws: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null

    const connect = () => {
      ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        setIsLive(true)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string)
          switch (data.type) {
            case 'issue_created':
            case 'issue_updated':
              queryClient.invalidateQueries({ queryKey: ['jira-summary', tenantId] })
              queryClient.invalidateQueries({ queryKey: ['jira-issues', tenantId] })
              if (data.key) {
                queryClient.invalidateQueries({ queryKey: ['jira-issue-detail', tenantId, data.key] })
              }
              break
            case 'comment_added':
              if (data.key) {
                queryClient.invalidateQueries({ queryKey: ['jira-issue-comments', tenantId, data.key] })
              }
              break
            case 'sync_completed':
            case 'sync_failed':
              queryClient.invalidateQueries({ queryKey: ['jira-sync-status', tenantId] })
              break
            case 'webhook_processed':
              queryClient.invalidateQueries({ queryKey: ['jira-summary', tenantId] })
              queryClient.invalidateQueries({ queryKey: ['jira-issues', tenantId] })
              break
          }
        } catch {
          // Ignore malformed messages
        }
      }

      ws.onclose = () => {
        setIsLive(false)
        reconnectTimer = setTimeout(connect, 5000)
      }

      ws.onerror = () => {
        ws?.close()
      }
    }

    connect()

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer)
      ws?.close()
      setIsLive(false)
    }
  }, [tenantId, queryClient])

  return { isLive }
}

export function useJiraSprints(state?: string) {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-sprints', tenantId, state],
    queryFn: () => jiraApi.getSprints(tenantId!, state),
    enabled: !!tenantId,
    staleTime: 60_000,
  })
}

export function useJiraVelocity(limit = 6) {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-velocity', tenantId, limit],
    queryFn: () => jiraApi.getVelocity(tenantId!, limit),
    enabled: !!tenantId,
    staleTime: 120_000,
  })
}

export function useJiraCustomFields() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  return useQuery({
    queryKey: ['jira-custom-fields', tenantId],
    queryFn: () => jiraApi.getCustomFields(tenantId!),
    enabled: !!tenantId,
  })
}
